/**
 * Predictive Search — Moon Moon Agency
 * Custom element handling fetch + results/empty state toggle
 */
class PredictiveSearch extends HTMLElement {
	constructor() {
		super();

		this.input = this.querySelector('input[type="search"]');
		this.predictiveSearchResults = this.querySelector('#predictive-search');
		this.predictiveSearchDefault = this.querySelector('#predictive-search-default');
		this.predictiveSearchEmpty = this.querySelector('#predictive-search-empty');

		/** @type {AbortController | null} */
		this._abort = null;

		this._loader = document.createElement('div');
		this._loader.className = 'mm-search-loader';
		const modalBody = this.querySelector('.mm-search-modal-body');
		(modalBody || this).appendChild(this._loader);

		if (!this.input) return;

		this.input.addEventListener(
			'input',
			this._debounce((event) => this._onChange(event), 300).bind(this)
		);
	}

	_onChange() {
		const searchTerm = this.input.value.trim();

		if (!searchTerm.length) {
			this.close();
			return;
		}

		this._fetchResults(searchTerm);
	}

	_fetchResults(searchTerm) {
		this._loader.style.display = 'flex';
		if (this.predictiveSearchResults) this.predictiveSearchResults.style.display = 'none';
		if (this._abort) this._abort.abort();
		this._abort = new AbortController();

		const signal = this._abort.signal;
		const localeRoot = window.Shopify?.routes?.root ?? '/';
		const q = encodeURIComponent(searchTerm);

		// /search prend en compte les metafields "searchable" de Search & Discovery,
		// contrairement à /search/suggest — parité avec la page de résultats.
		// options[prefix]=last : matching au fil de la frappe sur le dernier terme.
		const primaryUrl = `${localeRoot}search?q=${q}&type=product&options[prefix]=last&options[unavailable_products]=last&section_id=predictive-search-products`;
		const fallbackUrl = `${localeRoot}search/suggest?q=${q}&section_id=predictive-search&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last`;

		const fetchPrimary = () =>
			fetch(primaryUrl, { signal })
				.then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); })
				.then((text) => ({ text, sectionId: 'predictive-search-products' }))
				.catch((err) => {
					if (err?.name === 'AbortError') throw err;
					return fetch(fallbackUrl, { signal })
						.then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); })
						.then((text) => ({ text, sectionId: 'predictive-search' }));
				});

		fetchPrimary()
			.then(({ text, sectionId }) => {
				const doc = new DOMParser().parseFromString(text, 'text/html');
				const sectionRoot = doc.querySelector(`#shopify-section-${sectionId}`);

				if (!sectionRoot || !this.predictiveSearchResults) return;

				const resultsData = sectionRoot.querySelector('[data-has-results]');

				if (resultsData) {
					const container = this.predictiveSearchResults.querySelector('[data-results-container]');
					if (container) container.innerHTML = resultsData.innerHTML;

					const seeAllLink = this.predictiveSearchResults.querySelector('[data-see-all-link]');
					if (seeAllLink && resultsData.dataset.resultsUrl) {
						seeAllLink.href = resultsData.dataset.resultsUrl;
					}

					/* Re-scan les .splide injectes (sliders d'images per-card) — DOMContentLoaded a deja fire.
					 * Meme pattern que mm_grid_cards.js apres recommendations fetch. */
					window.SplideManager?.init?.();

					this._loader.style.display = 'none';
					this._open();
				} else {
					this._loader.style.display = 'none';
					this._showEmpty(searchTerm);
				}
			})
			.catch((error) => {
				this._loader.style.display = 'none';
				if (error?.name === 'AbortError') return;
				this.close();
			});
	}

	_open() {
		if (this.predictiveSearchResults) this.predictiveSearchResults.style.display = 'flex';
		if (this.predictiveSearchDefault) this.predictiveSearchDefault.style.display = 'none';
		if (this.predictiveSearchEmpty) this.predictiveSearchEmpty.style.display = 'none';
	}

	_showEmpty(searchTerm) {
		if (this.predictiveSearchResults) this.predictiveSearchResults.style.display = 'none';
		if (this.predictiveSearchDefault) this.predictiveSearchDefault.style.display = 'none';

		if (this.predictiveSearchEmpty) {
			const titleEl = this.predictiveSearchEmpty.querySelector('[data-empty-title]');
			if (titleEl) {
				const template = titleEl.dataset.titleTemplate || titleEl.textContent;
				titleEl.dataset.titleTemplate = template;
				titleEl.textContent = template.replace('[terms]', searchTerm);
			}
			this.predictiveSearchEmpty.style.display = 'flex';
		}
	}

	close() {
		if (this._abort) {
			this._abort.abort();
			this._abort = null;
		}
		this._loader.style.display = 'none';
		if (this.predictiveSearchResults) this.predictiveSearchResults.style.display = 'none';
		if (this.predictiveSearchEmpty) this.predictiveSearchEmpty.style.display = 'none';
		if (this.predictiveSearchDefault) {
			this.predictiveSearchDefault.style.opacity = '0';
			this.predictiveSearchDefault.style.display = 'flex';
			this.predictiveSearchDefault.classList.remove('mm-none');
			requestAnimationFrame(() => {
				this.predictiveSearchDefault.style.opacity = '1';
			});
		}
	}

	_debounce(fn, wait) {
		let t;
		return (...args) => {
			clearTimeout(t);
			t = setTimeout(() => fn.apply(this, args), wait);
		};
	}
}

customElements.define('mm-predictive-search', PredictiveSearch);
